/* Coupon strip state machine: idle -> pending -> claimed, plus two verified
 * detours — 'other' (the order carries the shopper's own code; the strip
 * offers a swap and names the consequence) and back to idle when the cart
 * disproves a remembered claim or the drawer's x removes the code.
 *
 * Ported verbatim from the production Broadcast store (assets/afroyla-coupon-
 * strip.js) — the whole module is already theme-agnostic: document-level
 * CustomEvents (afroyla:*) and data attributes only, no Broadcast-specific
 * selectors. The one change from production: the idle/other hint now shows
 * the computed payable price (phase-06 PDP sim copy, owner-approved
 * 2026-08-20), instead of the generic "Tap to add it to your order".
 *
 * Why claimed is remembered locally — and verified. The local flag survives
 * things the discount does not: carts expire, a checkout consumes the code,
 * the drawer's own chip can remove it. cart.discount_codes lists every code
 * attached to the session (even on an empty cart), so reconcile() checks the
 * flag against it on every page: code missing -> re-attach it once; the cart
 * still refuses (dead code, or the shopper applied a different one, which is
 * never overwritten) -> the flag is cleared and the strip goes back to idle.
 *
 * The strip is order-level: one claim flips every strip on the page carrying
 * the same code, including a quick-add modal opened over the page. */
(function () {
  'use strict';

  // Theme-editor section re-renders re-execute this script; without a guard
  // the document-level click/discount/variant listeners stack on every
  // re-render.
  if (window.__aflCouponStripInit) return;
  window.__aflCouponStripInit = true;

  var STRIP = '[data-coupon-strip]';
  var BTN = '[data-coupon-strip-btn]';
  var MINUS = '−';
  var TIMEOUT_MS = 8000;

  var inFlight = {};
  var cartPromise = null;

  /* --- storage: private mode throws, and that must not break the strip --- */

  function flagKey(code) {
    return 'afroyla:coupon:' + code;
  }

  function readFlag(code) {
    try {
      return window.localStorage.getItem(flagKey(code)) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeFlag(code) {
    try {
      window.localStorage.setItem(flagKey(code), '1');
    } catch (e) {
      /* Claimed still shows for this page life. */
    }
  }

  function clearFlag(code) {
    try {
      window.localStorage.removeItem(flagKey(code));
    } catch (e) {
      /* Nothing stored, nothing to clear. */
    }
  }

  /* --- money ---------------------------------------------------------- */

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* Mirrors the Liquid exactly: `price | times: pct | divided_by: 100` is
   * integer division, so the saving floors and the payable is derived from
   * it. Getting this wrong by a cent is the bug the strip exists to end. */
  function savingsFor(priceCents, strip) {
    var fixed = parseInt(strip.getAttribute('data-fixed-cents') || '0', 10);
    var pct = parseFloat(strip.getAttribute('data-discount-pct') || '0');
    var saving = 0;
    if (fixed > 0) {
      saving = fixed;
    } else if (pct > 0) {
      saving = Math.floor((priceCents * pct) / 100);
    }
    return Math.min(saving, priceCents);
  }

  function payableFor(strip) {
    var price = parseInt(strip.getAttribute('data-price-cents') || '0', 10);
    return price - savingsFor(price, strip);
  }

  /* --- rendering ------------------------------------------------------- */

  function stripsFor(code) {
    var all = document.querySelectorAll(STRIP);
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute('data-coupon-code') === code) out.push(all[i]);
    }
    return out;
  }

  function paint(strip) {
    var state = strip.getAttribute('data-state') || 'idle';
    var code = strip.getAttribute('data-coupon-code') || '';
    var money = formatMoney(parseInt(strip.getAttribute('data-savings-cents') || '0', 10));
    var payable = formatMoney(payableFor(strip));

    var title = strip.querySelector('[data-coupon-title]');
    var value = strip.querySelector('[data-coupon-value]');
    var hint = strip.querySelector('[data-coupon-hint]');
    var btn = strip.querySelector(BTN);
    if (!title || !value || !hint || !btn) return;

    if (state === 'claimed') {
      title.textContent = 'Coupon added';
      value.textContent = MINUS + money + ' applied';
      hint.textContent = 'Comes off at checkout';
    } else {
      title.textContent = 'Coupon available';
      value.textContent = MINUS + money + ' with ' + code;
      if (state === 'pending') {
        hint.textContent = 'Adding your coupon…';
      } else if (state === 'error') {
        hint.textContent = 'Could not add the coupon — tap to try again';
      } else if (state === 'other') {
        // The order carries the shopper's own code. Codes stack on this
        // store — /discount/X appends to discount_codes, it does not evict —
        // so the honest consequence to state is that her code survives the
        // tap. Kept to one line so the box never resizes under a finger.
        var other = strip.getAttribute('data-other-code');
        hint.textContent = other
          ? 'Tap to add — ' + other + ' stays'
          : 'Tap to add it — you pay ' + payable + '.';
      } else {
        hint.textContent = 'Tap to add it — you pay ' + payable + '.';
      }
    }

    if (state === 'error') {
      hint.classList.add('hc-coupon__hint--error');
    } else {
      hint.classList.remove('hc-coupon__hint--error');
    }

    btn.disabled = state === 'pending';
    btn.setAttribute('aria-busy', state === 'pending' ? 'true' : 'false');
    if (state === 'claimed') {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
  }

  function setState(strip, state) {
    strip.setAttribute('data-state', state);
    paint(strip);
  }

  /* --- cart reconciliation --------------------------------------------- */

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function cartOnce() {
    if (!cartPromise) {
      cartPromise = fetchCart();
    }
    return cartPromise;
  }

  function titleMatches(title, code) {
    return typeof title === 'string' && title.toLowerCase() === code.toLowerCase();
  }

  function cartHasCode(cart, code) {
    if (!cart || !cart.items) return false;

    var appliedTop = cart.cart_level_discount_applications || cart.discount_applications || [];
    for (var a = 0; a < appliedTop.length; a++) {
      if (titleMatches(appliedTop[a] && appliedTop[a].title, code)) return true;
    }

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var discounts = item.discounts || [];
      for (var d = 0; d < discounts.length; d++) {
        if (titleMatches(discounts[d] && discounts[d].title, code)) return true;
      }
      var allocations = item.line_level_discount_allocations || [];
      for (var l = 0; l < allocations.length; l++) {
        var app = allocations[l] && allocations[l].discount_application;
        if (titleMatches(app && app.title, code)) return true;
      }
    }
    return false;
  }

  function attachment(cart, code) {
    var codes = cart && cart.discount_codes;
    if (Object.prototype.toString.call(codes) === '[object Array]') {
      var otherCode = '';
      for (var i = 0; i < codes.length; i++) {
        if (!codes[i] || !codes[i].code) continue;
        if (titleMatches(codes[i].code, code)) {
          return { known: true, attached: true, other: false, otherCode: '' };
        }
        if (!otherCode) otherCode = codes[i].code;
      }
      return { known: true, attached: false, other: otherCode !== '', otherCode: otherCode };
    }
    return { known: false, attached: cartHasCode(cart, code), other: false, otherCode: '' };
  }

  function demote(code) {
    clearFlag(code);
    var strips = stripsFor(code);
    for (var i = 0; i < strips.length; i++) {
      strips[i].removeAttribute('data-other-code');
      setState(strips[i], 'idle');
    }
  }

  function confirmClaim(code, cart) {
    writeFlag(code);
    var strips = stripsFor(code);
    for (var i = 0; i < strips.length; i++) {
      strips[i].removeAttribute('data-other-code');
      setState(strips[i], 'claimed');
    }
    document.dispatchEvent(new CustomEvent('afroyla:coupon:claimed', { detail: { code: code } }));
    if (cart && cart.item_count > 0) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
    }
  }

  var reapplyTried = {};

  function reconcile(strip) {
    var code = strip.getAttribute('data-coupon-code');
    if (!code) return;

    cartOnce().then(function (cart) {
      if (!cart) return;
      var att = attachment(cart, code);

      if (att.attached) {
        writeFlag(code);
        var strips = stripsFor(code);
        for (var i = 0; i < strips.length; i++) setState(strips[i], 'claimed');
        // This restore path runs AFTER the passive consumers' initial paint
        // (deferred scripts execute in DOM order; the pill/upsell/set-picker
        // painted while the strip still said idle). Without a dispatch they
        // never repaint and the button keeps quoting full price against a
        // claimed strip. Same event they already subscribe to for unclaim.
        document.dispatchEvent(new CustomEvent('afroyla:discount:applied', { detail: { code: code } }));
        return;
      }

      if (att.other) {
        clearFlag(code);
        var offered = stripsFor(code);
        for (var j = 0; j < offered.length; j++) {
          offered[j].setAttribute('data-other-code', att.otherCode);
          setState(offered[j], 'other');
        }
        return;
      }

      if (!readFlag(code) || !att.known) return;

      if (reapplyTried[code]) {
        demote(code);
        return;
      }
      reapplyTried[code] = true;

      fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
        .then(function () {
          return fetchCart();
        })
        .then(function (fresh) {
          var again = attachment(fresh, code);
          if (again.attached) {
            confirmClaim(code, fresh);
          } else if (again.known) {
            demote(code);
          }
        });
    });
  }

  /* --- the one-way transition ------------------------------------------ */

  function claim(strip) {
    var code = strip.getAttribute('data-coupon-code');
    var state = strip.getAttribute('data-state');
    if (!code || inFlight[code] || state === 'pending' || state === 'claimed') return;

    inFlight[code] = true;
    var strips = stripsFor(code);
    var i;
    for (i = 0; i < strips.length; i++) setState(strips[i], 'pending');

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, TIMEOUT_MS);

    var options = { credentials: 'same-origin' };
    if (controller) options.signal = controller.signal;

    fetch('/discount/' + encodeURIComponent(code), options)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return fetchCart();
      })
      .then(function (cart) {
        var att = attachment(cart, code);
        if (att.known && !att.attached) throw new Error('code not attached');
        confirmClaim(code, cart);
      })
      .catch(function () {
        var failed = stripsFor(code);
        for (var k = 0; k < failed.length; k++) setState(failed[k], 'error');
      })
      .then(function () {
        window.clearTimeout(timer);
        delete inFlight[code];
      });
  }

  /* --- lifecycle -------------------------------------------------------- */

  function initStrip(strip) {
    if (strip.__afroylaCoupon) return;
    strip.__afroylaCoupon = true;

    var code = strip.getAttribute('data-coupon-code');
    if (code && readFlag(code)) {
      setState(strip, 'claimed');
    } else {
      paint(strip);
    }
    reconcile(strip);
  }

  function initAll(root) {
    var strips = (root || document).querySelectorAll(STRIP);
    for (var i = 0; i < strips.length; i++) initStrip(strips[i]);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest(BTN);
    if (!btn) return;
    var strip = btn.closest(STRIP);
    if (!strip) return;
    event.preventDefault();
    claim(strip);
  });

  /* The cart drawer's discount controls (phase-07) announce what actually
   * happened to the cart — only then may the strip's memory move. */
  document.addEventListener('afroyla:discount:removed', function (event) {
    var removed = ((event.detail && event.detail.code) || '').trim();
    if (!removed) return;

    clearFlag(removed);
    var strips = document.querySelectorAll(STRIP);
    for (var i = 0; i < strips.length; i++) {
      var code = strips[i].getAttribute('data-coupon-code');
      if (code && titleMatches(removed, code)) {
        demote(code);
      } else if (titleMatches(strips[i].getAttribute('data-other-code'), removed)) {
        strips[i].removeAttribute('data-other-code');
        setState(strips[i], 'idle');
      }
    }
  });

  document.addEventListener('afroyla:discount:applied', function (event) {
    var applied = ((event.detail && event.detail.code) || '').trim();
    if (!applied) return;

    var strips = document.querySelectorAll(STRIP);
    for (var i = 0; i < strips.length; i++) {
      var strip = strips[i];
      var code = strip.getAttribute('data-coupon-code');
      if (code && titleMatches(applied, code)) {
        writeFlag(code);
        strip.removeAttribute('data-other-code');
        setState(strip, 'claimed');
      } else {
        var state = strip.getAttribute('data-state');
        if (state === 'idle' || state === 'error' || state === 'other') {
          strip.setAttribute('data-other-code', applied);
          setState(strip, 'other');
        }
      }
    }
  });

  /* A variant switch changes the price, so it changes the saving. Scope to
   * the nearest ancestor holding a strip, so a quick-add modal over a
   * product page cannot repaint both buy boxes. */
  document.addEventListener('theme:variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || typeof variant.price !== 'number') return;

    var scope = document;
    for (var el = event.target; el && el.nodeType === 1; el = el.parentElement) {
      if (el.querySelector && el.querySelector(STRIP)) {
        scope = el;
        break;
      }
    }

    var strips = scope.querySelectorAll(STRIP);
    for (var i = 0; i < strips.length; i++) {
      strips[i].setAttribute('data-price-cents', variant.price);
      strips[i].setAttribute('data-savings-cents', savingsFor(variant.price, strips[i]));
      paint(strips[i]);
    }
  });

  initAll(document);

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(STRIP)) initStrip(node);
          else if (node.querySelector && node.querySelector(STRIP)) initAll(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
